import fetch from 'dva/fetch';
import { notification, message as antdMessage } from 'antd';
import { getSceneInfo } from '@/utils/logicUtils';
import {
  SSO_LOCATION_FOR_LOGIN,
  SSO_CALLBACK_PATH_FOR_LOGIN,
  DATA_CENTER_PORTAL_PATH,
} from '@/configs/path';
import { ajaxError, ERROR_LEVEL, reportApi } from '@/utils/owl';
import qs from 'qs';

export const PLATFORM_HOST = `//${window.location.host}`;

const codeMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

/**
 * 按照 HTTP Status 处理请求结果
 * @param response
 * @returns {*}
 */
const checkStatus = response => {
  // 200 - 299 返回 response
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  // 300 - 399 重定向
  else if (response.status >= 300 && response.status < 400) {
    const { origin, pathname, href } = window.location;
    const callbackHref = `${origin}${pathname}${SSO_CALLBACK_PATH_FOR_LOGIN}?cb_target=${encodeURIComponent(
      href
    )}`;
    window.location.href = `${SSO_LOCATION_FOR_LOGIN}${encodeURIComponent(callbackHref)}`;
    return;
  }
  // 其他 弹窗提示异常并 throw
  const errorText = response.msg || codeMessage[response.status] || response.statusText;
  notification.error({
    message: `请求错误 ${response.status}: ${response.url}`,
    description: errorText,
  });
  const error = new Error(errorText);
  error.name = response.status;
  error.response = response;
  throw error;
};

/**
 * @param {Promise<{ code, msg, data, [status] }>} response
 * @param {any} [errorHandler] code != 200 时会调用，处理异常，为空时弹出 message 提示
 * @param {{ returnAllRes: boolean }} [config] returnAllRes 时会跳过 errorHandler，直接返回
 * @returns {Promise<undefined|*>}
 */
const format = async (response, errorHandler, config = {}) => {
  const { code, msg, data, status } = (await response) || {};
  // sso 若请求 header 中带有 'Content-Type': 'application/json; charset=utf-8', 请求 status 会返回 200，而返回体中的 status 字段为 401
  if (status === 401) {
    const { origin, pathname, href } = window.location;
    const callbackHref = `${origin}${pathname}${SSO_CALLBACK_PATH_FOR_LOGIN}?cb_target=${encodeURIComponent(
      href
    )}`;
    window.location.href = `${SSO_LOCATION_FOR_LOGIN}${encodeURIComponent(callbackHref)}`;
    return;
  }
  // 如果配置参数returnAllRes是true返回所有的response
  if (config.returnAllRes) return response;
  if (code !== 200 && status !== 200) {
    const formatMessage = msg || codeMessage[code] || '服务正忙，请稍后再试';
    if (typeof errorHandler === 'function') {
      const handlerResult = await errorHandler({ code, msg: formatMessage, data });
      if (handlerResult) {
        return handlerResult;
      }
    } else {
      antdMessage.error(formatMessage);
    }
    return undefined;
  } else if (config.unwrap) {
    return response;
  } else {
    return data;
  }
};

export { format };

/**
 * Requests a URL, returning a promise.
 *
 * @param  {string} url       The URL we want to request
 * @param  {{ headers, body }} [options] The options we want to pass to "fetch"
 * @return {object}           An object containing either "data" or "err"
 */
export default function request(url, options = {}) {
  const defaultOptions = {
    credentials: 'include',
  };
  const newOptions = { ...defaultOptions, ...options };
  // 添加全局灰度标识
  if (window.poisgcgray) {
    newOptions.headers = {
      poisgcgray: window.poisgcgray,
      ...newOptions.headers,
    };
  }
  // 处理 Form / JSON 格式的请求体，添加相应 header
  if (
    newOptions.method === 'POST' ||
    newOptions.method === 'PUT' ||
    newOptions.method === 'PATCH' ||
    newOptions.method === 'DELETE'
  ) {
    if (!(newOptions.body instanceof FormData)) {
      newOptions.headers = {
        Accept: 'application/json',
        'x-requested-with': 'XMLHttpRequest',
        'Content-Type': 'application/json; charset=utf-8',
        ...newOptions.headers,
      };
      try {
        newOptions.body = JSON.stringify(newOptions.body);
      } catch (e) {
        console.log(e);
      }
    } else {
      // newOptions.body is FormData
      newOptions.headers = {
        Accept: 'application/json',
        'x-requested-with': 'XMLHttpRequest',
        ...newOptions.headers,
      };
    }
  }

  // 处理路径参数上的 client，将其作为路径参数，替换掉原有的 /mdc 前缀
  if (window.mdcUtmSource && window.mdcUtmSource !== 'mdc') {
    // 只替换路径前缀，避免误替换 /mdcMerchant 等
    if (url.startsWith('/mdc/')) {
      url = url.replace('/mdc', `/${window.mdcUtmSource}`);
    }
  }

  // 开头没有协议的 url，补全后端 API 服务链接
  if (!/^http(s)?:/.test(url)) {
    url = `${PLATFORM_HOST}${DATA_CENTER_PORTAL_PATH}${url}`;
  }

  // protal-portm 请求不能配置credentials: include
  if (/^http(s)?:/.test(url) && /portal-portm/.test(url)) {
    newOptions.credentials = 'same-origin';
  }

  // 把场景信息填充到 body 里面
  if (newOptions.__SCENE_INFO__ && newOptions.body) {
    delete newOptions.__SCENE_INFO__;
    if (typeof newOptions.body === 'string') {
      const temp = JSON.parse(newOptions.body);
      temp.sceneInfo = Object.assign(getSceneInfo(), temp.sceneInfo);
      newOptions.body = JSON.stringify(temp);
    } else if (typeof newOptions.body === 'object') {
      newOptions.body.sceneInfo = Object.assign(getSceneInfo(), newOptions.body.sceneInfo);
    }
  }

  const startTime = Date.now();
  return fetch(url, newOptions)
    .then(response => {
      reportApi(url, newOptions, response.clone(), Date.now() - startTime);
      return checkStatus(response);
    })
    .then(response => {
      let res = null;
      try {
        // DELETE and 204 do not return data by default
        // using .json will report an error.
        if (response.status === 204) {
          res = response.text();
        } else {
          res = response.json();
        }
      } catch (e) {
        console.error(e);
        throw e;
      }
      return res;
    })
    .catch(e => {
      const status = e.name;
      // 后端返回未登录
      if (status === 401) {
        // 跳转 sso 登录界面
        const { origin, pathname, href } = window.location;
        const callbackHref = `${origin}${pathname}${SSO_CALLBACK_PATH_FOR_LOGIN}?cb_target=${encodeURIComponent(
          href
        )}`;
        window.location.href = `${SSO_LOCATION_FOR_LOGIN}${encodeURIComponent(callbackHref)}`;
      }
    });
}
